import { BookingsExport } from "./bookingsExportData";

/**
 * Anteprima degli export in una nuova scheda del browser.
 *
 * La scheda va aperta in modo sincrono nel click (altrimenti il popup
 * blocker la ferma), poi, finita la generazione, viene reindirizzata a una
 * pagina HTML costruita al volo: il PDF nel visualizzatore nativo del
 * browser, l'Excel come tabella leggibile. In entrambi i casi il file non
 * viene scaricato finché l'utente non preme "Scarica".
 */

const escapeHtml = (value: string | number | undefined | null): string =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const blobToDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });

const BASE_CSS = `
  :root { color-scheme: light; }
  * { box-sizing: border-box; }
  html, body { margin: 0; height: 100%; }
  body {
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
    background: #f1f5f9; color: #0f172a; font-size: 14px;
    display: flex; flex-direction: column;
  }
  .bar {
    display: flex; align-items: center; justify-content: space-between; gap: 16px;
    padding: 10px 20px; background: #fff; border-bottom: 1px solid #e2e8f0;
    box-shadow: 0 1px 8px rgba(15, 23, 42, 0.06); flex: 0 0 auto;
  }
  .bar-title { display: flex; flex-direction: column; min-width: 0; }
  .bar-title strong { font-size: 15px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .bar-title span { font-size: 12px; color: #64748b; }
  .actions { display: flex; gap: 8px; flex: 0 0 auto; }
  .btn {
    display: inline-flex; align-items: center; gap: 8px; padding: 8px 14px;
    border-radius: 8px; border: 1px solid #e2e8f0; background: #fff; color: #0f172a;
    font-weight: 600; font-size: 13px; text-decoration: none; cursor: pointer;
  }
  .btn:hover { background: #f8fafc; }
  .btn-primary { background: #f25d0b; border-color: #f25d0b; color: #fff; }
  .btn-primary:hover { background: #d94f06; }
  .content { flex: 1 1 auto; min-height: 0; }
  iframe { display: block; width: 100%; height: 100%; border: 0; background: #525659; }
`;

const EXCEL_CSS = `
  .content { overflow: auto; padding: 24px; }
  .sheet {
    max-width: 1400px; margin: 0 auto; background: #fff; border: 1px solid #e2e8f0;
    border-radius: 12px; box-shadow: 0 4px 18px rgba(15, 23, 42, 0.06); overflow: hidden;
  }
  .sheet-head { padding: 20px 24px 16px; border-bottom: 3px solid #f25d0b; }
  .sheet-head h1 { margin: 0 0 4px; font-size: 20px; letter-spacing: -0.01em; }
  .sheet-head p { margin: 0; color: #64748b; font-size: 13px; }
  .kpis { display: flex; flex-wrap: wrap; gap: 10px; padding: 16px 24px 4px; }
  .kpi { border: 1px solid #e2e8f0; border-radius: 8px; padding: 8px 12px; min-width: 120px; }
  .kpi small { display: block; font-size: 10px; font-weight: 600; letter-spacing: .06em; text-transform: uppercase; color: #64748b; }
  .kpi b { font-size: 18px; display: inline-flex; align-items: center; gap: 6px; }
  .kpi i { width: 8px; height: 8px; border-radius: 50%; display: inline-block; }
  .kpi.accent { background: #0f172a; border-color: #0f172a; color: #fff; }
  .kpi.accent small { color: #cbd5e1; }
  .tabs { display: flex; gap: 4px; padding: 16px 24px 0; }
  .tab { font-size: 12px; font-weight: 600; color: #64748b; padding: 6px 12px; border-radius: 6px 6px 0 0; border: 1px solid #e2e8f0; border-bottom: 0; background: #f8fafc; }
  .tab.active { background: #fff; color: #0f172a; }
  .table-wrap { overflow-x: auto; margin: 0 24px 24px; border: 1px solid #e2e8f0; border-radius: 0 8px 8px 8px; }
  table { border-collapse: collapse; width: 100%; font-size: 12.5px; }
  th { background: #0f172a; color: #fff; text-align: left; padding: 9px 12px; font-size: 11px; font-weight: 600; letter-spacing: .04em; white-space: nowrap; position: sticky; top: 0; }
  td { padding: 8px 12px; border-bottom: 1px solid #f1f5f9; vertical-align: top; white-space: nowrap; }
  td.wrap { white-space: normal; min-width: 220px; }
  tr:nth-child(even) td { background: #f8fafc; }
  td.time { font-weight: 700; }
  td.type { font-weight: 600; border-left: 4px solid transparent; }
  td.muted, tr.cancelled td { color: #94a3b8; }
  tr.cancelled td.time, tr.cancelled td.type, tr.cancelled td.who { text-decoration: line-through; }
  td.empty { color: #94a3b8; font-style: italic; }
  .summary { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 16px; padding: 0 24px 24px; }
  .summary h3 { font-size: 13px; margin: 0 0 8px; }
  .summary table td:last-child, .summary table th:last-child { text-align: right; }
  .summary table td:nth-child(2), .summary table th:nth-child(2) { text-align: right; }
`;

const loadingHtml = (label: string) => `<!doctype html>
<html lang="it"><head><meta charset="utf-8"><title>${escapeHtml(label)}</title>
<style>${BASE_CSS}
  .loading { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 14px; color: #64748b; }
  .spinner { width: 36px; height: 36px; border-radius: 50%; border: 3px solid #e2e8f0; border-top-color: #f25d0b; animation: spin .8s linear infinite; }
  @keyframes spin { to { transform: rotate(360deg); } }
</style></head>
<body><div class="loading"><div class="spinner"></div><div>Preparo l'anteprima…</div></div></body></html>`;

const navigateTo = (win: Window, html: string) => {
  const url = URL.createObjectURL(new Blob([html], { type: "text/html" }));
  win.location.replace(url);
};

/** Da chiamare in modo sincrono dentro l'handler del click. */
export const openPreviewWindow = (label: string): Window | null => {
  const win = window.open("", "_blank");
  if (!win) return null;
  try {
    win.document.open();
    win.document.write(loadingHtml(label));
    win.document.close();
  } catch {
    // Alcuni browser non consentono di scrivere nella scheda vuota: pazienza,
    // verrà comunque reindirizzata quando il file è pronto.
  }
  return win;
};

export const closePreviewWindow = (win: Window | null) => {
  try {
    win?.close();
  } catch {
    /* ignora */
  }
};

export const buildPdfPreviewHtml = (
  data: BookingsExport,
  fileName: string,
  viewUrl: string,
  downloadUrl: string
) => `<!doctype html>
<html lang="it"><head><meta charset="utf-8"><title>${escapeHtml(fileName)}</title>
<style>${BASE_CSS}</style></head>
<body>
  <div class="bar">
    <div class="bar-title">
      <strong>${escapeHtml(fileName)}</strong>
      <span>${escapeHtml(data.structureName)} · ${escapeHtml(data.dateLong)} · ${
    data.totals.bookings
  } prenotazioni</span>
    </div>
    <div class="actions">
      <a class="btn btn-primary" href="${downloadUrl}" download="${escapeHtml(
    fileName
  )}">Scarica PDF</a>
    </div>
  </div>
  <div class="content"><iframe src="${viewUrl}" title="${escapeHtml(
    fileName
  )}"></iframe></div>
</body></html>`;

export const showPdfPreview = async (
  win: Window,
  blob: Blob,
  fileName: string,
  data: BookingsExport
) => {
  const viewUrl = URL.createObjectURL(blob);
  const downloadUrl = await blobToDataUrl(blob);
  navigateTo(win, buildPdfPreviewHtml(data, fileName, viewUrl, downloadUrl));
};

const kpiHtml = (data: BookingsExport) => {
  const items = [
    `<div class="kpi accent"><small>Prenotazioni</small><b>${data.totals.bookings}</b></div>`,
    `<div class="kpi"><small>Campi in uso</small><b>${data.totals.facilitiesWithBookings}/${data.totals.facilities}</b></div>`,
    ...data.totals.byType.map(
      (t) =>
        `<div class="kpi"><small>${escapeHtml(t.label)}</small><b><i style="background:${escapeHtml(
          t.color
        )}"></i>${t.count}</b></div>`
    ),
  ];
  return `<div class="kpis">${items.join("")}</div>`;
};

const bookingsTableHtml = (data: BookingsExport) => {
  const head = [
    "Campo",
    "Tipo campo",
    "Orario",
    "Tipologia",
    "Formato",
    "Prenotato da",
    "Contatto",
    "Squadra A",
    "Squadra B",
    "Coach",
    "Note",
    "Stato",
    "Visibilità",
  ];
  const rows: string[] = [];
  for (const f of data.facilities) {
    if (f.rows.length === 0) {
      rows.push(
        `<tr><td>${escapeHtml(f.name)}</td><td>${escapeHtml(
          f.typeLabel
        )}</td><td class="muted">—</td><td class="empty" colspan="10">Nessuna prenotazione</td></tr>`
      );
      continue;
    }
    for (const r of f.rows) {
      rows.push(
        `<tr class="${r.isCancelled ? "cancelled" : ""}">
          <td>${escapeHtml(f.name)}</td>
          <td>${escapeHtml(f.typeLabel)}</td>
          <td class="time">${escapeHtml(r.time)}</td>
          <td class="type" style="border-left-color:${escapeHtml(r.color)};color:${escapeHtml(
          r.color
        )}">${escapeHtml(r.typeLabel)}</td>
          <td>${escapeHtml(r.matchTypeLabel)}</td>
          <td class="who">${escapeHtml(r.bookedBy)}</td>
          <td>${escapeHtml(r.contact)}</td>
          <td>${escapeHtml(r.team1.join(", "))}</td>
          <td>${escapeHtml(r.team2.join(", "))}</td>
          <td>${escapeHtml(r.coach)}</td>
          <td class="wrap">${escapeHtml(r.notes)}</td>
          <td>${escapeHtml(r.statusLabel)}</td>
          <td>${escapeHtml(r.scopeLabel)}</td>
        </tr>`
      );
    }
  }
  return `<div class="table-wrap"><table>
    <thead><tr>${head.map((h) => `<th>${h}</th>`).join("")}</tr></thead>
    <tbody>${rows.join("")}</tbody>
  </table></div>`;
};

const summaryHtml = (data: BookingsExport) => {
  const perFacility = data.facilities
    .map(
      (f) =>
        `<tr><td>${escapeHtml(f.name)}</td><td>${f.rows.length}</td><td>${escapeHtml(
          f.typeLabel
        )}</td></tr>`
    )
    .join("");
  const perType = data.totals.byType
    .map(
      (t) =>
        `<tr><td style="color:${escapeHtml(t.color)};font-weight:600">${escapeHtml(
          t.label
        )}</td><td>${t.count}</td><td>${
          data.totals.bookings
            ? Math.round((t.count / data.totals.bookings) * 100)
            : 0
        }%</td></tr>`
    )
    .join("");
  return `<div class="summary">
    <div><h3>Prenotazioni per campo</h3><div class="table-wrap" style="margin:0;border-radius:8px"><table>
      <thead><tr><th>Campo</th><th>Prenotazioni</th><th>Tipo</th></tr></thead>
      <tbody>${perFacility}<tr><td><b>Totale</b></td><td><b>${data.totals.bookings}</b></td><td></td></tr></tbody>
    </table></div></div>
    <div><h3>Prenotazioni per tipologia</h3><div class="table-wrap" style="margin:0;border-radius:8px"><table>
      <thead><tr><th>Tipologia</th><th>Prenotazioni</th><th>Quota</th></tr></thead>
      <tbody>${perType || `<tr><td class="empty" colspan="3">Nessuna prenotazione</td></tr>`}</tbody>
    </table></div></div>
  </div>`;
};

export const buildExcelPreviewHtml = (
  data: BookingsExport,
  fileName: string,
  downloadUrl: string
) => {
  const subtitle = [
    data.structureName,
    data.sportName,
    `generato il ${data.generatedAt}`,
  ]
    .filter(Boolean)
    .join("  ·  ");

  const html = `<!doctype html>
<html lang="it"><head><meta charset="utf-8"><title>${escapeHtml(fileName)}</title>
<style>${BASE_CSS}${EXCEL_CSS}</style></head>
<body>
  <div class="bar">
    <div class="bar-title">
      <strong>${escapeHtml(fileName)}</strong>
      <span>Anteprima del foglio Excel · ${data.totals.bookings} prenotazioni</span>
    </div>
    <div class="actions">
      <a class="btn btn-primary" href="${downloadUrl}" download="${escapeHtml(
    fileName
  )}">Scarica Excel</a>
    </div>
  </div>
  <div class="content">
    <div class="sheet">
      <div class="sheet-head">
        <h1>Prenotazioni del ${escapeHtml(data.dateLong)}</h1>
        <p>${escapeHtml(subtitle)}</p>
      </div>
      ${kpiHtml(data)}
      <div class="tabs"><span class="tab active">Prenotazioni</span></div>
      ${bookingsTableHtml(data)}
      <div class="tabs"><span class="tab active">Riepilogo</span></div>
      ${summaryHtml(data)}
    </div>
  </div>
</body></html>`;
  return html;
};

export const showExcelPreview = async (
  win: Window,
  blob: Blob,
  fileName: string,
  data: BookingsExport
) => {
  const downloadUrl = await blobToDataUrl(blob);
  navigateTo(win, buildExcelPreviewHtml(data, fileName, downloadUrl));
};
